
import React, { useState, useEffect } from 'react';
import CountryFlag from '../../shared/components/CountryFlag';
import {
    Empty, Card, Form, Input, Button, Tabs, message, Avatar,
    Upload, Typography, Space, Divider, Alert, List, Select,
    Modal, Tag, Spin, Popconfirm
} from 'antd';
import {
    UserOutlined, MailOutlined, LockOutlined,
    SaveOutlined, UploadOutlined, BellOutlined,
    SecurityScanOutlined, ApiOutlined, SafetyOutlined, CopyOutlined, InboxOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import NotificationSettings from './account/Notifications';
import NotificationInbox from './account/NotificationInbox';
import LoginMethods from './account/LoginMethods';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../shared/hooks/useAuth';
import userService from '../services/userService';
import authService from '../../shared/services/authService';
import apiClient from '../../shared/services/apiClient';
import phoneCountryCodes from '../../shared/utils/phoneCountryCodes';
import { formatDateTime } from '../../shared/utils/date';
import { useTranslation } from '../../shared/i18n';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Settings = () => {
    const { t } = useTranslation();
    const { currentUser, updateUser, updateTokens } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = ['profile', 'security', 'notifications', 'inbox', 'api-keys'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'profile';
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [apiKeysForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [apiKeys, setApiKeys] = useState([]);
    const [newApiKey, setNewApiKey] = useState(null);
    const [currencies, setCurrencies] = useState([]);

    // ============ 两步验证 (2FA) 状态 ============
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    // OAuth-only accounts have no password yet: "Set password" instead of "Change password"
    const [hasPassword, setHasPassword] = useState(true);
    const [loginMethodsKey, setLoginMethodsKey] = useState(0);
    const [codeSending, setCodeSending] = useState(false);
    const [codeCooldown, setCodeCooldown] = useState(0);
    const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
    const [twoFactorStatusLoading, setTwoFactorStatusLoading] = useState(true);
    const [twoFactorLoading, setTwoFactorLoading] = useState(false);
    const [setupData, setSetupData] = useState(null); // {secret, otpauth_uri}
    const [setupVisible, setSetupVisible] = useState(false);
    const [backupCodes, setBackupCodes] = useState(null); // 启用成功后一次性展示
    const [disableVisible, setDisableVisible] = useState(false);
    const [enableForm] = Form.useForm();
    const [disableForm] = Form.useForm();

    // Email change: re-auth → code to the new address → confirm
    const [emailForm] = Form.useForm();
    const [emailVisible, setEmailVisible] = useState(false);
    const [emailCodeSent, setEmailCodeSent] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [emailConfirming, setEmailConfirming] = useState(false);
    const [emailCooldown, setEmailCooldown] = useState(0);

    useEffect(() => {
        if (emailCooldown <= 0) return undefined;
        const id = setTimeout(() => setEmailCooldown((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [emailCooldown]);

    const openEmailModal = () => {
        emailForm.resetFields();
        setEmailCodeSent(false);
        setEmailVisible(true);
    };

    const sendEmailChangeCode = async () => {
        let values;
        try {
            values = await emailForm.validateFields(['new_email', 'current_password', 'totp_code']);
        } catch (_) {
            return;
        }
        try {
            setEmailSending(true);
            const res = await apiClient.post('/api/user/email/change-request', {
                email: values.new_email,
                current_password: values.current_password,
                totp_code: values.totp_code,
            }, { skipGlobalErrorToast: true });
            message.success(res.data?.message || t('Code sent'));
            setEmailCodeSent(true);
            setEmailCooldown(60);
        } catch (e) {
            if (e.response?.data?.requires_2fa) setTwoFactorEnabled(true);
            message.error(e.response?.data?.error || t('Failed to send the email'));
        } finally {
            setEmailSending(false);
        }
    };

    const confirmEmailChange = async () => {
        let values;
        try {
            values = await emailForm.validateFields(['email_code']);
        } catch (_) {
            return;
        }
        try {
            setEmailConfirming(true);
            const res = await apiClient.post('/api/user/email/change-confirm', {
                code: values.email_code,
                refresh_token: localStorage.getItem('refresh_token') || undefined,
            }, { skipGlobalErrorToast: true });
            // Other sessions are signed out; this one gets fresh tokens.
            if (!res.data?.refresh_token) localStorage.removeItem('refresh_token');
            updateTokens({ accessToken: res.data?.token, refreshToken: res.data?.refresh_token });
            if (res.data?.data) updateUser(res.data.data);
            message.success(res.data?.message || t('Email changed'));
            setEmailVisible(false);
        } catch (e) {
            message.error(e.response?.data?.error || t('Incorrect or expired code'));
        } finally {
            setEmailConfirming(false);
        }
    };

    // 加载 2FA 状态
    useEffect(() => {
        fetchTwoFactorStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTwoFactorStatus = async () => {
        try {
            setTwoFactorStatusLoading(true);
            const result = await authService.get2faStatus();
            if (result.success) {
                setTwoFactorEnabled(!!result.data?.enabled);
                setHasPassword(result.data?.has_password !== false);
                setBackupCodesRemaining(result.data?.backup_codes_remaining || 0);
            }
        } finally {
            setTwoFactorStatusLoading(false);
        }
    };

    // 开始启用流程：获取密钥 + 二维码
    const startTwoFactorSetup = async () => {
        try {
            setTwoFactorLoading(true);
            const result = await authService.setup2fa();
            if (result.success) {
                setSetupData(result.data);
                setBackupCodes(null);
                enableForm.resetFields();
                setSetupVisible(true);
            } else {
                message.error(result.error || result.message || t('Failed to initialize two-step verification'));
            }
        } finally {
            setTwoFactorLoading(false);
        }
    };

    // 输入验证码确认启用
    const handleEnableTwoFactor = async (values) => {
        try {
            setTwoFactorLoading(true);
            const result = await authService.enable2fa(values.code);
            if (result.success) {
                message.success(t('Two-factor authentication enabled'));
                setBackupCodes(result.data?.backup_codes || []);
                setTwoFactorEnabled(true);
                fetchTwoFactorStatus();
            } else {
                message.error(result.error || result.message || t('Failed to enable two-step verification'));
            }
        } finally {
            setTwoFactorLoading(false);
        }
    };

    // 停用两步验证（需密码 + 验证码/备用码）
    const handleDisableTwoFactor = async (values) => {
        try {
            setTwoFactorLoading(true);
            const result = await authService.disable2fa(values.password, values.code);
            if (result.success) {
                message.success(t('Two-factor authentication disabled'));
                setDisableVisible(false);
                disableForm.resetFields();
                setTwoFactorEnabled(false);
                fetchTwoFactorStatus();
            } else {
                message.error(result.error || result.message || t('Failed to disable two-factor authentication'));
            }
        } finally {
            setTwoFactorLoading(false);
        }
    };

    const copyBackupCodes = () => {
        if (!backupCodes) return;
        navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
            message.success(t('Backup codes copied to clipboard'));
        }).catch(() => {
            message.error(t('Failed to copy. Please copy manually.'));
        });
    };

    const closeSetupModal = () => {
        setSetupVisible(false);
        setSetupData(null);
        setBackupCodes(null);
        enableForm.resetFields();
    };

    // Load currencies
    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                const response = await apiClient.get('/currencies');
                if (response.data.success) {
                    setCurrencies(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching currencies:', error);
            }
        };
        fetchCurrencies();
    }, []);

    useEffect(() => {
        if (currentUser) {
            profileForm.setFieldsValue({
                username: currentUser.username,
                email: currentUser.email,
                phone: currentUser.phone,
                phone_country_code: currentUser.phone_country_code || '+86',
                company: currentUser.company,
                currency: currentUser.currency || 'USD',
            });

            setAvatarUrl(currentUser.avatar_url);

            // 获取API密钥
            fetchApiKeys();
        }
    }, [currentUser]);

    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const response = await userService.getApiKeys();
            if (response.success) {
                setApiKeys(response.data);
            }
        } catch (error) {
            console.error('Error fetching API keys:', error);
            message.error(t('Unable to retrieve API key'));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateProfile = async (values) => {
        try {
            setLoading(true);
            const profile = { ...values };
            delete profile.email; // email changes go through the Change dialog
            const response = await userService.updateProfile(profile);
            if (response.success) {
                message.success(t('Profile updated'));
                updateUser(response.data);
            } else {
                message.error(response.error || t('Failed to update profile'));
            }
        } catch (error) {
            message.error(t('Failed to update profile: {{error}}', { error: error.message }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (codeCooldown <= 0) return undefined;
        const id = setTimeout(() => setCodeCooldown((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [codeCooldown]);

    // First password of an OAuth-only account: a code goes to the account email
    const sendPasswordSetupCode = async () => {
        try {
            setCodeSending(true);
            const res = await apiClient.post('/api/user/password/setup-code', {}, { skipGlobalErrorToast: true });
            message.success(res.data?.message || t('Code sent'));
            setCodeCooldown(60);
        } catch (e) {
            message.error(e.response?.data?.error || t('Failed to send the email'));
        } finally {
            setCodeSending(false);
        }
    };

    const handleUpdatePassword = async (values) => {
        try {
            setLoading(true);
            const response = await userService.changePassword(values.current_password, values.new_password, hasPassword ? undefined : values.email_code);
            if (response.success) {
                message.success(hasPassword ? t('Password updated') : t('Password set'));
                passwordForm.resetFields();
                if (!hasPassword) {
                    setHasPassword(true);
                    setLoginMethodsKey((k) => k + 1);
                }
            } else {
                message.error(response.error || t('Failed to update password'));
            }
        } catch (error) {
            message.error(error?.error ? t(error.error) : t('Failed to update password: {{error}}', { error: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarChange = async (info) => {
        if (info.file.status === 'uploading') {
            setLoading(true);
            return;
        }

        if (info.file.status === 'done') {
            setLoading(false);
            const response = info.file.response;
            if (response.success) {
                setAvatarUrl(response.data.url);
                message.success(t('Avatar updated'));
                updateUser({
                    ...currentUser,
                    avatar_url: response.data.url
                });
            } else {
                message.error(response.error || t('Failed to upload avatar'));
            }
        } else if (info.file.status === 'error') {
            setLoading(false);
            message.error(t('Failed to upload avatar'));
        }
    };

    const generateApiKey = async () => {
        try {
            const values = await apiKeysForm.validateFields();
            setLoading(true);
            const response = await userService.generateApiKey(values.description, values.expires_in_days);
            if (response.success) {
                apiKeysForm.resetFields();
                setNewApiKey(response.data.api_key);
                fetchApiKeys();
            } else {
                message.error(response.error || t('Failed to generate API key'));
            }
        } catch (error) {
            if (error?.errorFields) return;
            message.error(t('Failed to generate API key: {{error}}', { error: error.error || error.message || '' }));
        } finally {
            setLoading(false);
        }
    };

    const deleteApiKey = async (keyId) => {
        try {
            setLoading(true);
            const response = await userService.deleteApiKey(keyId);
            if (response.success) {
                message.success(t('API key revoked'));
                fetchApiKeys();
            } else {
                message.error(response.error || t('Failed to revoke API key'));
            }
        } catch (error) {
            message.error(t('Failed to revoke API key: {{error}}', { error: error.error || error.message || '' }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Title level={2}>{t('Profile')}</Title>

            <Tabs activeKey={activeTab} onChange={(k) => setSearchParams(k === 'profile' ? {} : { tab: k }, { replace: true })}>
                <TabPane
                    tab={<span><UserOutlined />{t('Profile')}</span>}
                    key="profile"
                >
                    <Card>
                        <div style={{ display: 'flex', marginBottom: 24 }}>
                            <div style={{ marginRight: 24 }}>
                                <Avatar
                                    size={96}
                                    icon={<UserOutlined />}
                                    src={avatarUrl}
                                />
                                <div style={{ marginTop: 16 }}>
                                    <Upload
                                        name="avatar"
                                        action="/api/user/avatar"
                                        showUploadList={false}
                                        onChange={handleAvatarChange}
                                    >
                                        <Button icon={<UploadOutlined />}>{t('Change avatar')}</Button>
                                    </Upload>
                                </div>
                            </div>

                            <div style={{ flex: 1 }}>
                                <Form
                                    form={profileForm}
                                    layout="vertical"
                                    onFinish={handleUpdateProfile}
                                    className="form-container"
                                >
                                    <Form.Item
                                        name="username"
                                        label={t('Username')}
                                        rules={[{ required: true, message: t('Enter a username') }]}
                                    >
                                        <Input prefix={<UserOutlined />} disabled />
                                    </Form.Item>

                                    <Form.Item label={t('Email address')}>
                                        <Space.Compact style={{ width: '100%' }}>
                                            <Form.Item name="email" noStyle>
                                                <Input prefix={<MailOutlined />} readOnly />
                                            </Form.Item>
                                            <Button onClick={openEmailModal}>{t('Change')}</Button>
                                        </Space.Compact>
                                    </Form.Item>

                                    <Form.Item label={t('Mobile number')}>
                                        <Input.Group compact>
                                            <Form.Item name="phone_country_code" noStyle>
                                                <Select
                                                    style={{ width: 140 }}
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        String(option.text || '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                >
                                                    {phoneCountryCodes.map(item => (
                                                        <Select.Option key={item.code} value={item.code} text={`${item.code} ${t(item.label)} ${item.short}`}>
                                                            <CountryFlag code={item.short} />{item.code} {t(item.label)}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item
                                                name="phone"
                                                noStyle
                                                rules={[
                                                    {
                                                        pattern: /^[0-9\s-]{5,20}$/,
                                                        message: t('Enter a valid phone number (digits, spaces or hyphens only, 5-20 characters)'),
                                                    },
                                                ]}
                                            >
                                                <Input style={{ width: 'calc(100% - 140px)' }} placeholder={t('Mobile number')} />
                                            </Form.Item>
                                        </Input.Group>
                                    </Form.Item>

                                    <Form.Item
                                        name="company"
                                        label={t('Company name')}
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        name="currency"
                                        label={t('Preferred currency')}
                                        extra={t('Prices are shown in this currency as estimates. Bills and balance stay in USD.')}
                                    >
                                        <Select
                                            placeholder={t('Select a currency')}
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {currencies.map(currency => (
                                                <Select.Option key={currency.code} value={currency.code}>
                                                    {currency.symbol} {currency.code} - {t(currency.name)}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            icon={<SaveOutlined />}
                                        >
                                            {t('Save changes')}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </div>
                        </div>
                    </Card>

                    <Modal
                        title={t('Change email')}
                        open={emailVisible}
                        onCancel={() => setEmailVisible(false)}
                        destroyOnClose
                        footer={emailCodeSent ? (
                            <Button type="primary" loading={emailConfirming} onClick={confirmEmailChange}>
                                {t('Confirm')}
                            </Button>
                        ) : null}
                    >
                        <Form form={emailForm} layout="vertical" preserve={false}>
                            <Form.Item
                                name="new_email"
                                label={t('New email')}
                                rules={[
                                    { required: true, message: t('Enter an email address') },
                                    { type: 'email', message: t('Enter a valid email address') }
                                ]}
                            >
                                <Input prefix={<MailOutlined />} disabled={emailCodeSent} autoComplete="email" />
                            </Form.Item>
                            {!emailCodeSent && (
                                <>
                                    <Form.Item
                                        name="current_password"
                                        label={t('Current password')}
                                        rules={[{ required: true, message: t('Please enter your current password') }]}
                                    >
                                        <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
                                    </Form.Item>
                                    {twoFactorEnabled && (
                                        <Form.Item
                                            name="totp_code"
                                            label={t('Two-factor code')}
                                            rules={[{ required: true, message: t('Enter your two-factor code') }]}
                                        >
                                            <Input prefix={<SafetyOutlined />} maxLength={6} inputMode="numeric" autoComplete="one-time-code" />
                                        </Form.Item>
                                    )}
                                    <Button type="primary" block onClick={sendEmailChangeCode} loading={emailSending} disabled={emailCooldown > 0}>
                                        {emailCooldown > 0 ? `${emailCooldown}s` : t('Send code')}
                                    </Button>
                                </>
                            )}
                            {emailCodeSent && (
                                <Form.Item
                                    name="email_code"
                                    label={t('Email code')}
                                    rules={[{ required: true, pattern: /^\d{6}$/, message: t('Enter the 6-digit code from your email') }]}
                                >
                                    <Input prefix={<MailOutlined />} maxLength={6} inputMode="numeric" autoComplete="one-time-code" onPressEnter={confirmEmailChange} />
                                </Form.Item>
                            )}
                        </Form>
                    </Modal>
                </TabPane>

                <TabPane
                    tab={<span><LockOutlined />{t('Security settings')}</span>}
                    key="security"
                >
                    <Card>
                        <Title level={4}>{hasPassword ? t('Change password') : t('Set password')}</Title>
                        <Form
                            form={passwordForm}
                            layout="vertical"
                            onFinish={handleUpdatePassword}
                            className="form-container"
                        >
                            {hasPassword && (
                                <Form.Item
                                    name="current_password"
                                    label={t('Current password')}
                                    rules={[{ required: true, message: t('Please enter your current password') }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>
                            )}

                            {!hasPassword && (
                                <Form.Item label={t('Email code')} required>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Form.Item
                                            name="email_code"
                                            noStyle
                                            rules={[{ required: true, pattern: /^\d{6}$/, message: t('Enter the 6-digit code from your email') }]}
                                        >
                                            <Input prefix={<MailOutlined />} maxLength={6} inputMode="numeric" autoComplete="one-time-code" />
                                        </Form.Item>
                                        <Button onClick={sendPasswordSetupCode} loading={codeSending} disabled={codeCooldown > 0}>
                                            {codeCooldown > 0 ? `${codeCooldown}s` : t('Send code')}
                                        </Button>
                                    </Space.Compact>
                                </Form.Item>
                            )}

                            <Form.Item
                                name="new_password"
                                label={t('New password')}
                                rules={[
                                    { required: true, message: t('Please enter a new password') },
                                    { min: 8, message: t('Password must be at least 8 characters') },
                                    { pattern: /^(?=.*[A-Za-z])(?=.*\d)/, message: t('Password must contain both letters and numbers') }
                                ]}
                                hasFeedback
                                extra={t('At least 8 characters, including both letters and numbers')}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>

                            <Form.Item
                                name="confirm_password"
                                label={t('Confirm new password')}
                                dependencies={['new_password']}
                                hasFeedback
                                rules={[
                                    { required: true, message: t('Please confirm your new password') },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('new_password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error(t('Passwords do not match')));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    icon={<SaveOutlined />}
                                >
                                    {hasPassword ? t('Update password') : t('Set password')}
                                </Button>
                            </Form.Item>
                        </Form>

                        <LoginMethods key={loginMethodsKey} />

                        <Divider />

                        <Title level={4}>
                            {t('Two-factor authentication (2FA)')}{' '}
                            {twoFactorStatusLoading ? (
                                <Spin size="small" />
                            ) : twoFactorEnabled ? (
                                <Tag color="success">{t('Enabled')}</Tag>
                            ) : (
                                <Tag>{t('Disabled')}</Tag>
                            )}
                        </Title>

                        {twoFactorEnabled ? (
                            <Alert
                                message={t('Two-factor authentication enabled')}
                                description={
                                    <span>
                                        {t('When signing in, enter a one-time code from your authenticator app in addition to your password. Backup codes remaining:')}<Text strong>{backupCodesRemaining}</Text>{t(' items.')}
                                    </span>
                                }
                                type="success"
                                showIcon
                                action={
                                    <Button
                                        danger
                                        icon={<SecurityScanOutlined />}
                                        onClick={() => {
                                            disableForm.resetFields();
                                            setDisableVisible(true);
                                        }}
                                    >
                                        {t('Disable two-factor authentication')}
                                    </Button>
                                }
                                style={{ marginBottom: 16 }}
                            />
                        ) : (
                            <Alert
                                message={t('Two-factor authentication')}
                                description={t('Two-factor authentication improves your account security. When signing in, you will need your password and a one-time code from an authenticator app such as Google Authenticator.')}
                                type="info"
                                showIcon
                                action={
                                    <Button
                                        type="primary"
                                        icon={<SecurityScanOutlined />}
                                        loading={twoFactorLoading && !setupVisible}
                                        onClick={startTwoFactorSetup}
                                    >
                                        {t('Set up two-step verification')}
                                    </Button>
                                }
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {/* 启用流程 Modal：扫码 → 输入验证码 → 一次性展示备用码 */}
                        <Modal
                            title={backupCodes ? t('Save your backup codes') : t('Set up two-step verification')}
                            open={setupVisible}
                            onCancel={closeSetupModal}
                            footer={null}
                            maskClosable={false}
                            destroyOnClose
                        >
                            {backupCodes ? (
                                <div>
                                    <Alert
                                        message={t('Backup codes are shown only once!')}
                                        description={t('Please save the following backup codes now. You can use them to sign in when your authenticator is unavailable (each code can only be used once). You will not be able to view them again after closing this window.')}
                                        type="warning"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                    <Card size="small" style={{ marginBottom: 16 }}>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 8,
                                            fontFamily: 'monospace',
                                            fontSize: 16,
                                            textAlign: 'center'
                                        }}>
                                            {backupCodes.map(code => (
                                                <Text code key={code}>{code}</Text>
                                            ))}
                                        </div>
                                    </Card>
                                    <Space>
                                        <Button icon={<CopyOutlined />} onClick={copyBackupCodes}>
                                            {t('Copy all backup codes')}
                                        </Button>
                                        <Button type="primary" onClick={closeSetupModal}>
                                            {t('I\'ve saved it, finish')}
                                        </Button>
                                    </Space>
                                </div>
                            ) : setupData ? (
                                <div>
                                    <p>{t('1. Scan the QR code below with an authenticator app (Google Authenticator / 1Password / Authy, etc.):')}</p>
                                    <div style={{ textAlign: 'center', margin: '16px 0' }}>
                                        <QRCodeSVG value={setupData.otpauth_uri} size={180} />
                                    </div>
                                    <p>
                                        {t('Can\'t scan the code? Enter the key manually:')}<br />
                                        <Text code copyable style={{ wordBreak: 'break-all' }}>{setupData.secret}</Text>
                                    </p>
                                    <Divider style={{ margin: '12px 0' }} />
                                    <p>{t('2. Enter the 6-digit code from your authenticator app to finish enabling:')}</p>
                                    <Form form={enableForm} onFinish={handleEnableTwoFactor} layout="inline">
                                        <Form.Item
                                            name="code"
                                            rules={[
                                                { required: true, message: t('Enter the verification code') },
                                                { pattern: /^\d{6}$/, message: t('The verification code must be 6 digits') }
                                            ]}
                                        >
                                            <Input
                                                prefix={<SafetyOutlined />}
                                                placeholder={t('6-digit verification code')}
                                                maxLength={6}
                                                autoComplete="one-time-code"
                                                style={{ width: 180 }}
                                            />
                                        </Form.Item>
                                        <Form.Item>
                                            <Button type="primary" htmlType="submit" loading={twoFactorLoading}>
                                                {t('Enable')}
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </div>
                            ) : null}
                        </Modal>

                        {/* 停用流程 Modal：密码 + 验证码双重确认 */}
                        <Modal
                            title={t('Disable two-factor authentication')}
                            open={disableVisible}
                            onCancel={() => setDisableVisible(false)}
                            footer={null}
                            destroyOnClose
                        >
                            <Alert
                                message={t('Disabling will reduce your account security')}
                                description={t('To verify your identity, enter your account password and current verification code (or an unused backup code).')}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            <Form form={disableForm} layout="vertical" onFinish={handleDisableTwoFactor}>
                                {hasPassword && (
                                    <Form.Item
                                        name="password"
                                        label={t('Account password')}
                                        rules={[{ required: true, message: t('Enter your account password') }]}
                                    >
                                        <Input.Password prefix={<LockOutlined />} />
                                    </Form.Item>
                                )}
                                <Form.Item
                                    name="code"
                                    label={hasPassword ? t('Verification code / backup code') : t('Verification code')}
                                    rules={[{ required: true, message: hasPassword ? t('Enter a verification code or backup code') : t('Enter the verification code!') }]}
                                >
                                    <Input
                                        prefix={<SafetyOutlined />}
                                        placeholder={hasPassword ? t('6-digit verification code or 10-digit backup code') : t('6-digit verification code')}
                                        maxLength={hasPassword ? 10 : 6}
                                        autoComplete="one-time-code"
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Space>
                                        <Button danger type="primary" htmlType="submit" loading={twoFactorLoading}>
                                            {t('Confirm deactivation')}
                                        </Button>
                                        <Button onClick={() => setDisableVisible(false)}>{t('Cancel')}</Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </Modal>
                    </Card>
                </TabPane>

                <TabPane
                    tab={<span><BellOutlined />{t('Notification settings')}</span>}
                    key="notifications"
                >
                    <NotificationSettings />
                </TabPane>

                <TabPane
                    tab={<span><InboxOutlined />{t('Inbox')}</span>}
                    key="inbox"
                >
                    <NotificationInbox />
                </TabPane>

                <TabPane
                    tab={<span><ApiOutlined />{t('API key')}</span>}
                    key="api-keys"
                >
                    <Card>
                        <Alert
                            message={t('API key purpose')}
                            description={
                                <div>
                                    {t('API keys call the Yeap Cloud API on your behalf with standard user permissions. Use either request header:')}
                                    <div><Text code>Authorization: Bearer yk_…</Text>{t(' or ')}<Text code>X-API-Key: yk_…</Text></div>
                                    {t('Keys cannot access admin APIs, manage keys, change passwords, or manage two-step verification. Keys are shown only once, when created. Please revoke them when no longer needed.')}
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Title level={4}>{t('Generate new key')}</Title>
                        <Form
                            form={apiKeysForm}
                            layout="inline"
                            initialValues={{ expires_in_days: 365 }}
                            style={{ marginBottom: 16, rowGap: 8 }}
                        >
                            <Form.Item
                                name="description"
                                rules={[{ required: true, message: t('Please enter a key name') }]}
                                style={{ flex: 1, minWidth: 200 }}
                            >
                                <Input placeholder={t('Key name, e.g. CI deployment')} maxLength={100} />
                            </Form.Item>
                            <Form.Item name="expires_in_days">
                                <Select
                                    style={{ width: 140 }}
                                    options={[
                                        { value: 30, label: t('Expires in 30 days') },
                                        { value: 90, label: t('Expires in 90 days') },
                                        { value: 365, label: t('Expires in 1 year') },
                                        { value: 0, label: t('Never expires') },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    onClick={generateApiKey}
                                    loading={loading}
                                    icon={<ApiOutlined />}
                                >
                                    {t('Generate key')}
                                </Button>
                            </Form.Item>
                        </Form>

                        <Modal
                            open={!!newApiKey}
                            title={t('Please save your API key now')}
                            onCancel={() => setNewApiKey(null)}
                            footer={<Button type="primary" onClick={() => setNewApiKey(null)}>{t('I\'ve saved it')}</Button>}
                            maskClosable={false}
                        >
                            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('You will not be able to view this secret again after closing.')} />
                            <Text code copyable style={{ wordBreak: 'break-all' }}>{newApiKey}</Text>
                        </Modal>

                        <Divider />

                        <Title level={4}>{t('Existing key')}</Title>
                        {apiKeys.length === 0 ? (
                            <Empty description={t('No API keys yet')} />
                        ) : (
                            <List
                                dataSource={apiKeys}
                                renderItem={key => {
                                    const st = { active: ['green', t('Valid')], revoked: ['default', t('Revoked')], expired: ['orange', t('Expired')] }[key.status] || ['default', key.status];
                                    const fmt = (v) => (v ? formatDateTime(v) : '-');
                                    return (
                                        <List.Item
                                            actions={key.status === 'active' ? [
                                                <Popconfirm
                                                    key="revoke"
                                                    title={t('Programs using this key will stop working immediately after revocation. Revoke anyway?')}
                                                    onConfirm={() => deleteApiKey(key.id)}
                                                    okText={t('Revoke')}
                                                    okButtonProps={{ danger: true }}
                                                    cancelText={t('Cancel')}
                                                >
                                                    <Button type="text" danger>{t('Revoke')}</Button>
                                                </Popconfirm>
                                            ] : []}
                                        >
                                            <List.Item.Meta
                                                title={<Space>{key.name || key.description || 'API Key'}<Tag color={st[0]}>{st[1]}</Tag></Space>}
                                                description={
                                                    <Space direction="vertical" size={0}>
                                                        <Text type="secondary">{t('Key prefix: ')}<Text code>{key.key_prefix ? `${key.key_prefix}…` : t('Legacy key')}</Text></Text>
                                                        <Text type="secondary">{t('Created at: ')}{fmt(key.created_at)}{t(' · Expires: ')}{key.expires_at ? fmt(key.expires_at) : t('Never expires')}</Text>
                                                        <Text type="secondary">{t('Last used: ')}{key.last_used_at ? `${fmt(key.last_used_at)}${key.last_used_ip ? ` (${key.last_used_ip})` : ''}` : t('Never used')}</Text>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    );
                                }}
                            />
                        )}
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default Settings;
